import { newItem } from "core/redux/actions/localStorage/localStorageActions";
import type { AppStore } from "core/redux/store/configureStore.types";

export interface LocalStorageEntry<T> {
  id: string;
  data: T;
}

/** Typeguard for LocalStorageEntry with typed generic data field */
export const isLocalStorageEntry = <T>(id: string, entry?: LocalStorageEntry<unknown>): entry is LocalStorageEntry<T> =>
  entry?.id === id;

/** Generate a redux action whenever the local storage gets updated  */
export function listenToLocalStorage(store: AppStore) {
  window.addEventListener("storage", (e) => {
    if (e.newValue) {
      try {
        const parsedData = JSON.parse(e.newValue);
        store.dispatch(newItem({ id: e.key, data: parsedData } as LocalStorageEntry<unknown>));
      } catch {
        // ignore
      }
    }
  });
}

const localStorageItemKeys: string[] = [];

export function setLocalStorageItemKey(key: string) {
  localStorageItemKeys.push(key);
}

/** When loading the page, dispatch all values stored as action so they can be processed */
export function loadLocalStorage(store: AppStore) {
  [...localStorageItemKeys].forEach((key) => {
    const value = localStorage.getItem(key);
    if (value) {
      store.dispatch(newItem({ id: key, data: JSON.parse(value) } as LocalStorageEntry<unknown>));
    }
  });
}

/** Store a given id/data pair to localStorage */
export function setItem<T>(item: LocalStorageEntry<T>) {
  localStorage.setItem(item.id, JSON.stringify(item.data));
}

/** Get a given id/data pair from localStorage */
export function getItem<T>(key: string): T | undefined {
  const item = localStorage.getItem(key);
  return item ? (JSON.parse(item) as T) : undefined;
}

export function clearLocalStorage() {
  localStorage.clear();
}
